import {Component, OnInit} from '@angular/core';
import {ReferralService} from '../../../shared/services/referral.service';
import {ReferralApiService} from '../../../shared/services/referral-api.service';
import {RefPromptService} from '../shared/ref-prompt.component';

interface KycDocument {
    id: number;
    type: string;
    label: string;
}

interface KycRow {
    id: number;
    referrerName: string;
    referrerCode: string;
    panMasked: string;
    identityType: string;
    documents: KycDocument[];
}

// Matches config('referral.kyc.document_types') in the backend.
const DOCUMENT_LABEL: { [k: string]: string } = {
    PAN_CARD: 'PAN card image',
    IDENTITY_FRONT: 'ID proof (front)',
    IDENTITY_BACK: 'ID proof (back)',
    ADDRESS_PROOF: 'Address proof',
    BANK_PROOF: 'Cancelled cheque'
};
// What a card always offers a slot for, even when nothing's uploaded yet for that type.
const DISPLAY_DOCUMENT_TYPES = ['PAN_CARD', 'BANK_PROOF'];

@Component({
    selector: 'app-referral-kyc',
    templateUrl: './referral-kyc.component.html'
})
export class ReferralKycComponent implements OnInit {
    list: KycRow[] = [];
    loading = false;

    constructor(private referralService: ReferralService,
                private referralApi: ReferralApiService,
                private prompt: RefPromptService) {
    }

    ngOnInit() {
        this.load();
    }

    load() {
        this.loading = true;
        // SUBMITTED + UNDER_REVIEW: awaiting a decision (same bucket convention as payouts'
        // comma-separated status filter).
        this.referralApi.getKycRequests({status: 'SUBMITTED,UNDER_REVIEW', per_page: 100}).subscribe({
            next: (data: any) => {
                this.list = (data?.data ?? []).map((r: any) => this.toRow(r));
                this.loading = false;
            },
            error: () => {
                this.list = [];
                this.loading = false;
            }
        });
    }

    private toRow(r: any): KycRow {
        const uploaded: { [type: string]: number } = {};
        (r.documents ?? []).forEach((d: any) => {
            uploaded[d.document_type] = d.id;
        });
        const types = Array.from(new Set([...DISPLAY_DOCUMENT_TYPES, ...Object.keys(uploaded)]));

        return {
            id: r.id,
            referrerName: r.referrer?.name ?? '—',
            referrerCode: r.referrer?.referral_code ?? '',
            panMasked: r.pan_number,
            identityType: r.identity_type,
            documents: types.map(type => ({id: uploaded[type] ?? null, type, label: DOCUMENT_LABEL[type] ?? type}))
        };
    }

    openDocument(row: KycRow, doc: KycDocument) {
        if (!doc.id) {
            return;
        }
        this.referralApi.getKycDocumentBlob(row.id, doc.id).subscribe({
            next: (blob: Blob) => {
                const url = URL.createObjectURL(blob);
                window.open(url, '_blank', 'noopener');
                setTimeout(() => URL.revokeObjectURL(url), 60000);
            },
            error: () => {
                this.referralService.toast('Could not open document');
            }
        });
    }

    approve(row: KycRow) {
        this.referralApi.approveKyc(row.id).subscribe(() => {
            this.referralService.toast(`KYC approved for ${row.referrerName}`);
            this.load();
        });
    }

    async reject(row: KycRow) {
        const reason = await this.prompt.ask({
            title: `Reject KYC for ${row.referrerName}?`,
            text: 'The partner will see your reason and can upload the documents again.',
            label: 'Reason',
            okText: 'Reject KYC',
            check: v => v.trim().length >= 3 ? '' : 'Enter at least 3 characters.'
        });
        if (reason !== null) {
            this.referralApi.rejectKyc(row.id, {reason}).subscribe(() => {
                this.referralService.toast('KYC rejected');
                this.load();
            });
        }
    }
}
